import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { purchaseOrderPath, supplierReturnPath } from '../../lib/slug';
import { purchaseOrdersApi, poStatusMeta } from '../purchase-orders/purchaseOrdersApi';
import { receivingApi } from '../receiving/receivingApi';
import { returnsApi, RETURN_REASONS } from './returnsApi';
import '../requisitions/requisitions.css';

const todayIso = () => new Date().toISOString().slice(0, 10);

// Map a goods-receipt discrepancy reason to the closest return reason for prefill.
const GRN_REASON_TO_RETURN = {
  Damaged: 'Damaged',
  WrongItem: 'WrongItem',
  Expired: 'Expired',
  QualityIssue: 'QualityIssue',
  ShortDelivery: 'Other',
  OverDelivery: 'Overstock',
  Other: 'Other',
};

export default function ReturnFormPage() {
  const { id: routeRef } = useParams();
  const [searchParams] = useSearchParams();
  const grnRef = searchParams.get('grn');
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission, activePropertyId } = useProperty();
  const canManage = hasPermission('returns.manage');

  const [po, setPo] = useState(null);
  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);

  const [qty, setQty] = useState({});
  const [lineReason, setLineReason] = useState({});
  const [lineNote, setLineNote] = useState({});
  const [returnDate, setReturnDate] = useState(todayIso());
  const [headerReason, setHeaderReason] = useState('Damaged');
  const [locationId, setLocationId] = useState('');
  const [headerNotes, setHeaderNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const poData = await purchaseOrdersApi.get(routeRef);
      setPo(poData);
      setLocationId(poData.locationId || '');

      let draftGrn = null;
      if (grnRef) {
        draftGrn = await receivingApi.get(grnRef).catch(() => null);
        if (draftGrn && draftGrn.purchaseOrderId === poData.id) setGrn(draftGrn);
        else draftGrn = null;
      }

      // Prefill: from a GRN's accepted lines (discrepancy follow-up), otherwise blank.
      const seedQ = {};
      const seedReason = {};
      if (draftGrn) {
        (draftGrn.lines || []).forEach((gl) => {
          const accepted = Number(gl.receivedQuantity || 0);
          if (accepted > 0) seedQ[gl.purchaseOrderLineId] = String(accepted);
          if (gl.discrepancyReason && gl.discrepancyReason !== 'None') {
            seedReason[gl.purchaseOrderLineId] = GRN_REASON_TO_RETURN[gl.discrepancyReason] || 'Other';
          }
        });
      }
      setQty(seedQ);
      setLineReason(seedReason);
    } catch (err) {
      setError(err?.message || t('Purchase order not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, grnRef, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    catalogApi.listUnits().then((u) => { if (active) setUnits(u || []); }).catch(() => {});
    catalogApi.listLocations(activePropertyId).then((l) => { if (active) setLocations(l || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;

  // Only lines that have received goods can be returned.
  const lines = useMemo(() => (po?.lines || []).filter((l) => Number(l.receivedQuantity || 0) > 0), [po]);

  const { totalReturning, anyQty, hasOver } = useMemo(() => {
    let total = 0; let any = false; let over = false;
    lines.forEach((l) => {
      const q = Number(qty[l.lineId] || 0);
      if (q > 0) { total += q * Number(l.unitPrice?.amount || 0); any = true; }
      if (q > Number(l.receivedQuantity || 0)) over = true;
    });
    return { totalReturning: total, anyQty: any, hasOver: over };
  }, [lines, qty]);

  const returnable = po && (po.status === 'PartiallyReceived' || po.status === 'Closed');

  const setLineQtyValue = (lineId, value) => {
    if (value !== '' && Number(value) < 0) return;
    setQty((prev) => ({ ...prev, [lineId]: value }));
  };
  const setLineReasonValue = (lineId, value) => setLineReason((prev) => ({ ...prev, [lineId]: value }));
  const setLineNoteValue = (lineId, value) => setLineNote((prev) => ({ ...prev, [lineId]: value }));

  const returnAllReceived = () => {
    const next = {};
    lines.forEach((l) => { next[l.lineId] = String(Number(l.receivedQuantity || 0)); });
    setQty(next);
  };

  const buildPayload = () => {
    const payloadLines = lines
      .map((l) => ({
        purchaseOrderLineId: l.lineId,
        quantity: Number(qty[l.lineId] || 0),
        reason: lineReason[l.lineId] || headerReason,
        notes: (lineNote[l.lineId] || '').trim() || null,
      }))
      .filter((l) => l.quantity > 0);

    if (payloadLines.length === 0) {
      toast.error(t('Enter a return quantity on at least one line.'));
      return null;
    }

    return {
      purchaseOrderId: po.id,
      goodsReceiptId: grn?.id || null,
      returnDate: returnDate ? new Date(returnDate).toISOString() : null,
      reason: headerReason,
      locationId: locationId || null,
      notes: headerNotes.trim() || null,
      lines: payloadLines,
    };
  };

  const saveDraft = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setBusy(true);
    try {
      const saved = await returnsApi.create({ ...payload, post: false });
      toast.success(t('Draft return saved'));
      navigate(supplierReturnPath(saved));
    } catch (err) {
      toast.error(t('Could not save draft'), { description: err?.message });
      setBusy(false);
    }
  };

  const postReturn = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setBusy(true);
    try {
      const posted = await returnsApi.create({ ...payload, post: true });
      toast.success(t('Return {number} posted', { number: posted.number }));
      navigate(supplierReturnPath(posted));
    } catch (err) {
      toast.error(t('Could not post return'), { description: err?.message });
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !po) {
    return (
      <PageContainer size="full">
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load purchase order")}
          description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/returns')}>{t('Back to returns')}</Button>}
        />
      </PageContainer>
    );
  }

  const statusMeta = poStatusMeta(po.status);

  const renderLine = (l) => {
    const received = Number(l.receivedQuantity || 0);
    const returning = Number(qty[l.lineId] || 0);
    const over = returning > received;
    const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
    const meta = [
      l.sku,
      l.unitPrice ? money(l.unitPrice) : null,
      [l.costCenter, l.glAccountCode].filter(Boolean).join(' / ') || null,
    ].filter(Boolean).join(' · ');

    return (
      <div key={l.lineId} className={`rcv-line${returning > 0 ? ' is-flagged' : ''}`}>
        <div className="rcv-line__top">
          <div style={{ minWidth: 0 }}>
            <div className="rcv-line__name">{l.description}</div>
            {meta && <div className="rcv-line__meta">{meta}</div>}
          </div>
          <div className="rcv-line__stats">
            <div className="rcv-stat" title={t('Total ordered on this line')}>
              <span className="rcv-stat__label">{t('Ordered')}</span>
              <span className="rcv-stat__value">{l.quantity}{uom}</span>
            </div>
            <div className="rcv-stat" title={t('Quantity accepted into stock — the most you can return')}>
              <span className="rcv-stat__label">{t('Received')}</span>
              <span className="rcv-stat__value">{received}{uom}</span>
            </div>
          </div>
        </div>

        <div className="rcv-line__fields">
          <div className="rcv-field rcv-field--qty">
            <span className="rcv-field__label rcv-field__label--reject">{t('Return qty')}</span>
            <Input
              type="number"
              min="0"
              step="any"
              value={qty[l.lineId] ?? ''}
              onChange={(e) => setLineQtyValue(l.lineId, e.target.value)}
              wrapClassName={over ? 'is-over' : ''}
              placeholder="0"
              style={{ textAlign: 'right' }}
            />
          </div>
          {over && (
            <div className="rcv-field" style={{ alignSelf: 'center' }}>
              <Badge tone="danger">{t('More than received')}</Badge>
            </div>
          )}
          {returning > 0 && (
            <>
              <div className="rcv-field rcv-field--reason">
                <span className="rcv-field__label">{t('Reason')}</span>
                <Select
                  value={lineReason[l.lineId] || headerReason}
                  onChange={(v) => setLineReasonValue(l.lineId, v)}
                  ariaLabel={t('Return reason')}
                  options={RETURN_REASONS.map((r) => ({ ...r, label: t(r.label) }))}
                />
              </div>
              <div className="rcv-field rcv-field--note">
                <span className="rcv-field__label">{t('Note (optional)')}</span>
                <Input
                  value={lineNote[l.lineId] ?? ''}
                  onChange={(e) => setLineNoteValue(l.lineId, e.target.value)}
                  placeholder={t('e.g. 3 units spoiled on arrival')}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageContainer size="full">
      <Button
        variant="ghost"
        size="sm"
        leadingIcon={<Icon.ArrowLeft size={15} />}
        onClick={() => navigate(purchaseOrderPath(po))}
        style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}
      >
        {po.number}
      </Button>

      <PageHeader
        title={`${t('Return goods')} — ${po.number}`}
        description={
          <span>
            {po.supplierName || t('No supplier')} · <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge>
            {grn && <> · {t('from {number}', { number: grn.number })}</>}
          </span>
        }
        icon={<Icon.ArrowUpCircle size={22} />}
      />

      {!returnable && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">{t('Goods can only be returned against an order that has received deliveries. This order is {status}.', { status: t(statusMeta.label).toLowerCase() })}</Banner>
        </div>
      )}
      {lines.length === 0 && returnable && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">{t("No lines on this order have received stock yet, so there's nothing to return.")}</Banner>
        </div>
      )}
      {hasOver && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">{t('One or more lines return more than was received. Reduce the quantity to the received amount or less.')}</Banner>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
              <Input label={t('Return date')} type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
              <div className="ps-field">
                <span className="ps-field__label">{t('Reason')}</span>
                <Select value={headerReason} onChange={setHeaderReason} ariaLabel={t('Return reason')} options={RETURN_REASONS.map((r) => ({ ...r, label: t(r.label) }))} />
              </div>
              <div className="ps-field">
                <span className="ps-field__label">{t('Return stock from')}</span>
                <Select
                  value={locationId}
                  onChange={setLocationId}
                  ariaLabel={t('Return from location')}
                  placeholder={t('— Select location —')}
                  options={[{ value: '', label: t('— Select location —') }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
                  searchable
                />
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <div>
              <Card.Title>{t('Lines')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {t("Enter the quantity to return per line. Posting reverses that stock and decrements the order's received quantity. You can't return more than was received.")}
              </div>
            </div>
            {lines.length > 0 && (
              <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowUpCircle size={14} />} onClick={returnAllReceived}>{t('Return all received')}</Button>
            )}
          </Card.Header>
          <Card.Body>
            {lines.length === 0 ? (
              <EmptyState icon={<Icon.Package size={26} />} title={t('Nothing to return')} description={t('No lines on this order have received stock.')} />
            ) : (
              <div className="rcv-lines">
                {lines.map(renderLine)}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
          <Card.Body>
            <Textarea rows={2} value={headerNotes} onChange={(e) => setHeaderNotes(e.target.value)} placeholder={t('Why are these goods being returned? Reference any supplier RMA or collection details…')} />
          </Card.Body>
        </Card>

        <div className="req-form__actions" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="pp-cell-muted">{t('Return value:')} <strong>{money({ currency: po.currency, amount: totalReturning })}</strong></div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={() => navigate(purchaseOrderPath(po))} disabled={busy}>{t('Cancel')}</Button>
            <Button variant="secondary" loading={busy} disabled={!returnable || !canManage || !anyQty || hasOver} leadingIcon={<Icon.FileText size={15} />} onClick={saveDraft}>
              {t('Save draft')}
            </Button>
            <Button variant="primary" loading={busy} disabled={!returnable || !canManage || !anyQty || hasOver} leadingIcon={<Icon.ArrowUpCircle size={15} />} onClick={postReturn}>
              {t('Post return')}
            </Button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
